// lyrics-sync.js — pure helpers for resolving an active lyric line at a
// given playhead time. No DOM, no state. Mirrors the binary-search core
// of `LyricsEngine.cs` from the Unity port (and rhythmic mandarin web's
// `engines/lyrics-engine.js`) but stateless — the screen tracks the
// currently-displayed index itself.
//
// Field shape follows the V1 content contract (`docs/CONTENT-CONTRACT-
// 2026-05-04.md` and iOS `LyricsModel.h`):
//
//   {playtime: number, lyricEn: string, lyricZh: string}
//
// `playtime` is in seconds (the contract's `playtimeUnit: "seconds"`).

/**
 * @typedef {object} LyricLine
 * @property {number} playtime    seconds from audio start
 * @property {string} lyricEn
 * @property {string} lyricZh
 */

/**
 * Return the index of the lyric line that should be active at `timeSec`.
 * Highest index whose `playtime` ≤ `timeSec`. Returns 0 before the first
 * line's start and `lines.length - 1` past the last. Returns -1 when the
 * list is empty.
 *
 * Lines must be sorted by `playtime` ascending — the JSON loader is
 * responsible for that.
 *
 * @param {LyricLine[]} lines
 * @param {number} timeSec
 * @returns {number}
 */
export function findActiveLineIndex(lines, timeSec) {
  if (!Array.isArray(lines) || lines.length === 0) return -1;
  if (timeSec < lines[0].playtime) return 0;
  let lo = 0, hi = lines.length - 1, result = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid].playtime <= timeSec) { result = mid; lo = mid + 1; }
    else { hi = mid - 1; }
  }
  return result;
}

/**
 * Sort + normalise a lyric line array. Some authoring outputs may produce
 * out-of-order lines or non-finite `playtime` values; this returns a clean
 * copy safe to hand to `findActiveLineIndex`.
 *
 * @param {LyricLine[]} lines
 * @returns {LyricLine[]}
 */
export function normaliseLyricLines(lines) {
  if (!Array.isArray(lines)) return [];
  return lines
    .filter((l) => l && Number.isFinite(l.playtime))
    .slice()
    .sort((a, b) => a.playtime - b.playtime);
}
